// src/components/SimpleComponents/SimpleCep.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect, useMemo, useRef } from 'react';
import { Form } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { isValidCep, returnOnlyNumbers } from 'utils/helpers';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from './SimpleFormProvider';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import CepInput from 'layouts/SharedComponents/CepInput';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleCep = ({ name, subdocumentField, disabled, ...props }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const {
    locked,
    setFormZipState,
    fieldsWithError,
    disabledFields,
    handleAddressZipChange,
    data,
    form,
    formTmpState, // consume to trigger re-render when zip state changes
  } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName, flattenParentFieldName } = elementProps;

  // Use Form.useWatch to react to programmatic value changes (e.g., form.setFieldsValue)
  // This is needed for cross-profile import where data is loaded without triggering onChange
  const currentValue = Form.useWatch(fullFieldName, form);

  const dataValue = getValueFromFlattenObj(data, flattenFieldName);
  // Read zipState directly from formTmpState to ensure re-render when it changes
  const zipState = formTmpState?.zip?.[flattenFieldName] || null;
  const runOnce = useRef(false);

  const {
    customValidationStatus: baseStatus,
    customValidationMessage: baseMessage,
    handleChange,
  } = useCustomValidationStatus(
    {
      ...props,
      ...elementProps,
      // force 'validating' while API lookup is running
      loading: !!zipState?.searching,
    },
    isValidCep
  );

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------

  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // Calculate final status based on priorities
  const { finalStatus, finalMessage } = useMemo(() => {
    const cleanZip = returnOnlyNumbers(currentValue);
    const savedZip = returnOnlyNumbers(dataValue);

    // Priority 1: Validating (searching ZIP)
    if (zipState?.searching) {
      return { finalStatus: 'validating', finalMessage: '' };
    }

    // Priority 2: Empty field - check for required error from context
    if (!cleanZip) {
      const ctxMessageKey = fieldsWithError?.[flattenFieldName];
      if (ctxMessageKey) {
        return { finalStatus: 'error', finalMessage: translateX(ctxMessageKey) };
      }
      return { finalStatus: '', finalMessage: '' };
    }

    // Priority 3: Invalid ZIP format
    if (!isValidCep(cleanZip)) {
      return { finalStatus: 'error', finalMessage: baseMessage || translateX('invalid_zip') };
    }

    // Priority 4: Valid ZIP - always show success (ignore fieldsWithError for valid values)
    // This handles the case where data is loaded via cross-profile import
    // and fieldsWithError still has stale "required" errors
    return { finalStatus: 'success', finalMessage: '' };
  }, [currentValue, fieldsWithError, flattenFieldName, zipState, baseMessage]);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // PERFORMANCE FIX: Initialize zipState only once on mount if data exists
  useEffect(() => {
    if (runOnce.current || !data) return;

    const savedZip = returnOnlyNumbers(dataValue);

    // Only initialize if we have saved data and zipState is empty
    if (savedZip && !zipState?.data) {
      // Get the entire address object from data using flattenParentFieldName
      // This automatically handles root level ('') or nested paths ('address', 'moreAddress[0]', etc.)
      const addressData = flattenParentFieldName
        ? getValueFromFlattenObj(data, flattenParentFieldName)
        : data;

      // Initialize zipState with existing document data
      if (addressData) {
        setFormZipState(flattenFieldName, {
          data: addressData,
          lastSearchFound: savedZip,
          // Don't set apiFailure or searching - this is just initialization
        });
        runOnce.current = true;
      }
    }
  }, [dataValue, flattenFieldName, flattenParentFieldName, setFormZipState, data, zipState]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleAddressZipChangeWrapper = ev => {
    // Call the custom validation handler
    handleChange(ev);

    // Clear ZIP states when field is modified
    handleAddressZipChange?.({
      ev,
      ...elementProps,
      value: ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '',
      subdocumentField,
    });
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  // 53990-000 - Rua Eurico Cavalcanti de Albuquerque

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={finalStatus}
      customValidationMessage={finalMessage}
      // IMPORTANT: keep Form.Item feedback off to avoid AntD injecting suffix dynamically
      showFeedback={false}
    >
      <CepInput
        ref={elementRef}
        id={flattenFieldName}
        onChange={handleAddressZipChangeWrapper}
        disabled={isDisabled}
        status={finalStatus}
        loading={zipState?.searching || false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCep;

// *******************************************************************************
// HELPERS
// *******************************************************************************
